// The trader's terms, as a SUPPLIER reads them — one builder for every door.
//
// Owner decision 2026-09-28 (overrides the 2026-09-16 hiding): the supplier
// sees the target price, payment plan, delivery ask, sample requirement,
// quantity + unit, destination and timeframe. The trader's IDENTITY stays
// hidden — nothing here reads a name, a company or a contact.
//
// ONE BUILDER, ONE WORDING (golden rule 12). A supplier reaches a request
// through /f/<slug>, /rfq/<token>, the dashboard's routed list, the admin's
// preview of those, an email and the app. Each of them used to restate the
// labels and the formatting, which is how the dashboard came to say "Required"
// where the RFQ page said "Sample is mandatory", and how the delivery ask
// reached some doors as the trader's words ("To my address") — which, read by a
// supplier, names the SUPPLIER's address. Pure: no React, no Supabase, so the
// app mirrors it file-for-file and a test holds the wording still.
//
// INPUT is a request-shaped object in the DB's own column names, so a
// `requests` row, a get_factory_invite row and a get_rfq_details_for_supplier
// row all pass straight in. get_public_rfq names the country `destination`;
// that is accepted too.
//
// EVERY ROW IS CONDITIONAL. A null column means the trader was never asked
// (requests from before the field existed), and a row of '—' states nothing.

import {
  labelFor, REQUEST_UNITS, DELIVERY_TIMEFRAMES, SHIP_COUNTRIES, SAMPLE_REQUIREMENTS, cityLabel,
} from './requestFormOptions';
import { basisForPreference, deliveryFallsShort } from './deliveryMatch';
import { formatPriceLocale } from './formatLocale';

// PAYMENT PLANS ARE EXACTLY TWO since 2026-09-28 (owner): 30 (deposit, the
// balance before shipping) and 100 (paid in full). 50 is gone from every
// picker on web and app. Rows already stored with another value (50, 70, …)
// KEEP it — nothing rewrites them — and display through paymentPlanFallback
// as "X% upfront, balance before shipping", which is what they meant.
const COPY = {
  ar: {
    quantity: 'الكمية',
    targetPrice: 'السعر المستهدف',
    perUnit: (unit) => ` / ${unit}`,
    paymentTerms: 'شروط الدفع',
    paymentPlans: {
      30: '30% مقدّماً، والباقي قبل الشحن',
      100: '100% مقدّماً',
    },
    paymentPlanFallback: (pct) => `${pct}% مقدّماً، والباقي قبل الشحن`,
    delivery: 'طريقة الاستلام',
    sample: 'العينة',
    destination: 'الوجهة',
    timeframe: 'مدة التسليم',
    asks: {
      door: 'إلى باب التاجر — سعّر {basis}: الشحن والتخليص الجمركي والرسوم حتى عنوانه',
      port: 'إلى ميناء سعودي — سعّر {basis}: البضاعة والتأمين والشحن البحري حتى الميناء',
      factory: 'يستلمها من المصنع — سعّر {basis}: وكيل شحن التاجر يستلم من عندك',
    },
    shortfall: 'هذا أقل مما طلبه التاجر ({basis}). سيظهر عرضك له على أنه لا يطابق طلبه.',
    sep: '، ',
  },
  en: {
    quantity: 'Quantity',
    targetPrice: 'Target price',
    perUnit: (unit) => ` / ${unit}`,
    paymentTerms: 'Payment terms',
    paymentPlans: {
      30: '30% upfront, balance before shipping',
      100: '100% upfront',
    },
    paymentPlanFallback: (pct) => `${pct}% upfront, balance before shipping`,
    delivery: 'Delivery',
    sample: 'Sample',
    destination: 'Destination',
    timeframe: 'Delivery window',
    asks: {
      door: "To the trader's door — quote {basis}: freight, customs clearance and duties to their address",
      port: 'To a Saudi port — quote {basis}: goods, insurance and sea freight to the port',
      factory: "Collected at your factory — quote {basis}: the trader's forwarder picks up",
    },
    shortfall: 'This falls short of what the trader asked for ({basis}). Your offer will be shown to them as not matching their request.',
    sep: ', ',
  },
  zh: {
    quantity: '数量',
    targetPrice: '目标价格',
    perUnit: (unit) => ` / ${unit}`,
    paymentTerms: '付款条件',
    paymentPlans: {
      30: '预付 30%，余款发货前结清',
      100: '全额预付',
    },
    paymentPlanFallback: (pct) => `预付 ${pct}%，余款发货前结清`,
    delivery: '收货方式',
    sample: '样品',
    destination: '目的地',
    timeframe: '交货周期',
    asks: {
      door: '送到贸易商地址 — 请按 {basis} 报价：含运费、清关和关税，送至其地址',
      port: '送到沙特港口 — 请按 {basis} 报价：含货物、保险和海运费至港口',
      factory: '贸易商到工厂提货 — 请按 {basis} 报价：由其货代上门提货',
    },
    shortfall: '该条款未达到贸易商的要求（{basis}）。您的报价将向其标注为与需求不符。',
    sep: '、',
  },
};

const copyFor = (lang) => COPY[lang] || COPY.en;
const clean = (v) => String(v ?? '').trim();

/** Every label the builder uses, for callers that render a heading of their own. */
export const supplierTermsCopy = (lang) => copyFor(lang);

/**
 * What the trader asked for, told to the SUPPLIER, naming the Incoterm to
 * quote. '' for a missing or unknown preference — positive lookup (rule 12).
 */
export function deliveryAskText(deliveryPreference, lang) {
  const pref = clean(deliveryPreference).toLowerCase();
  const basis = basisForPreference(pref);
  const tpl = copyFor(lang).asks[pref];
  return basis && tpl ? tpl.replace('{basis}', basis) : '';
}

/**
 * The non-blocking warning under a quote form's basis picker. '' unless the
 * chosen basis stops short of what the trader asked (deliveryFallsShort) —
 * so `<Notice tone="warn">` renders nothing in every other case.
 */
export function deliveryShortfallText(priceBasis, deliveryPreference, lang) {
  if (!deliveryFallsShort(priceBasis, deliveryPreference)) return '';
  return copyFor(lang).shortfall.replace('{basis}', basisForPreference(deliveryPreference));
}

/**
 * The basis a quote form starts on: what the trader asked for, else the
 * supplier's single saved capability (`defaultBasisFor(terms)`, passed in so
 * this file stays free of Supabase), else ''.
 */
export const prefillPriceBasis = (deliveryPreference, fallback = '') =>
  basisForPreference(deliveryPreference) || fallback || '';

export const unitLabel = (unit, lang) => {
  const key = clean(unit);
  if (!key) return '';
  const hit = REQUEST_UNITS.find((u) => u.key === key);
  return hit ? labelFor(hit, lang) : key;
};

/** "12.50 SAR / Piece". '' when there is no target price. */
export function targetPriceText(req, lang) {
  const raw = req?.budget_per_unit;
  if (raw === null || raw === undefined || clean(raw) === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return '';
  const c = copyFor(lang);
  const cur = clean(req.budget_currency).toUpperCase();
  const u = unitLabel(req.unit, lang);
  return `${formatPriceLocale(n, lang)}${cur ? ` ${cur}` : ''}${u ? c.perUnit(u) : ''}`;
}

export function paymentPlanText(req, lang) {
  const c = copyFor(lang);
  const plan = clean(req?.payment_plan);
  if (plan && c.paymentPlans[plan]) return c.paymentPlans[plan];
  const pct = clean(req?.payment_pct) || plan;
  return pct && /^\d{1,3}$/.test(pct) ? c.paymentPlanFallback(pct) : '';
}

const countryLabel = (raw, lang) => {
  const v = clean(raw);
  if (!v) return '';
  const hit = SHIP_COUNTRIES.find((x) => x.key === v || x.en.toLowerCase() === v.toLowerCase() || x.ar === v);
  return hit && hit.key !== 'other' ? labelFor(hit, lang) : v;
};

export function destinationText(req, lang) {
  const city = cityLabel(req?.ship_city, lang);
  const country = countryLabel(req?.ship_country ?? req?.destination, lang);
  return [city, country].filter(Boolean).join(copyFor(lang).sep);
}

/** Display order, and the names callers pass to `only` / `omit`. */
export const SUPPLIER_TERM_KEYS = [
  'quantity', 'targetPrice', 'paymentPlan', 'delivery', 'sample', 'destination', 'timeframe',
];

/**
 * The rows, in display order: [{ key, label, value }], empty values dropped.
 *
 * @param {object} req   request-shaped row (DB column names)
 * @param {string} lang  'ar' | 'en' | 'zh'
 * @param {{ only?: string[], omit?: string[] }} [opts]
 *   only — render just these keys (the signed-out /rfq view: delivery only)
 *   omit — skip keys a page already renders its own way
 */
export function supplierTermRows(req, lang, { only, omit } = {}) {
  if (!req) return [];
  const c = copyFor(lang);
  const qty = clean(req.quantity);
  const unit = unitLabel(req.unit, lang);
  const sample = SAMPLE_REQUIREMENTS.find((s) => s.key === clean(req.sample_requirement));
  const tf = DELIVERY_TIMEFRAMES.find((d) => d.key === clean(req.delivery_timeframe));
  const values = {
    quantity: qty ? `${qty}${unit ? ` ${unit}` : ''}` : '',
    targetPrice: targetPriceText(req, lang),
    paymentPlan: paymentPlanText(req, lang),
    delivery: deliveryAskText(req.delivery_preference, lang),
    sample: sample ? labelFor(sample, lang) : '',
    destination: destinationText(req, lang),
    timeframe: tf ? labelFor(tf, lang) : clean(req.delivery_timeframe),
  };
  const labels = {
    quantity: c.quantity, targetPrice: c.targetPrice, paymentPlan: c.paymentTerms,
    delivery: c.delivery, sample: c.sample, destination: c.destination, timeframe: c.timeframe,
  };
  return SUPPLIER_TERM_KEYS
    .filter((k) => (!only || only.includes(k)) && !(omit || []).includes(k))
    .filter((k) => values[k])
    .map((k) => ({ key: k, label: labels[k], value: values[k] }));
}
